'use client';

import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';
import { MOBILE_QUERY, isTouchViewport } from '@/hooks/use-mobile';

const subscribeTouch = (onChange: () => void) => {
	const queries = [MOBILE_QUERY, '(pointer: coarse)'].map((query) => window.matchMedia(query));
	queries.forEach((mql) => mql.addEventListener('change', onChange));
	return () => queries.forEach((mql) => mql.removeEventListener('change', onChange));
};

/** Phones in either orientation, tablets and phone-width windows. */
const useTouchViewport = () => React.useSyncExternalStore(subscribeTouch, isTouchViewport, () => false);

// How the open popover behaves, decided when it opens (see Popover).
const PopoverModeContext = React.createContext<{ touch: boolean; modal: boolean } | null>(null);

/**
 * On touch screens a popover is modal unless the caller says otherwise: it
 * holds the scroll lock, so a picker opened inside a sheet or dialog scrolls
 * by touch (a non-modal popover sits outside the dialog's lock and can't),
 * and a tap outside closes it without also hitting whatever is underneath.
 *
 * The choice is made while the popover is closed and kept until it closes.
 * Radix renders modal and non-modal content as different components, so
 * switching while open (a phone rotating across 768px) would remount the
 * content and lose a typed search, the list's scroll position and focus.
 */
const Popover = ({
	modal,
	open: openProp,
	defaultOpen = false,
	onOpenChange,
	...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) => {
	const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
	const controlled = openProp !== undefined;
	const open = controlled ? openProp : uncontrolledOpen;
	const setOpen = React.useCallback(
		(next: boolean) => {
			if (!controlled) setUncontrolledOpen(next);
			onOpenChange?.(next);
		},
		[controlled, onOpenChange]
	);
	const isTouch = useTouchViewport();
	const [openedForTouch, setOpenedForTouch] = React.useState(isTouch);
	if (!open && openedForTouch !== isTouch) setOpenedForTouch(isTouch);
	const touch = open ? openedForTouch : isTouch;
	const resolvedModal = modal ?? touch;
	const mode = React.useMemo(() => ({ touch, modal: resolvedModal }), [touch, resolvedModal]);
	return (
		<PopoverModeContext.Provider value={mode}>
			<PopoverPrimitive.Root modal={resolvedModal} open={open} onOpenChange={setOpen} {...props} />
		</PopoverModeContext.Provider>
	);
};
Popover.displayName = 'Popover';

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
	React.ElementRef<typeof PopoverPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
		// Set to false to render inline (no portal). Useful inside a Dialog,
		// where the portal escapes the dialog's scroll-lock and breaks wheel
		// scrolling within the popover. A modal popover on a touch screen
		// always portals: it scrolls under its own lock, and inline it would
		// be clipped by the scrolling sheet or dialog.
		portal?: boolean;
	}
>(
	(
		{
			className,
			align = 'center' as const,
			sideOffset = 4,
			collisionPadding,
			portal = true,
			onOpenAutoFocus,
			...props
		},
		ref
	) => {
		const mode = React.useContext(PopoverModeContext);
		const viewportTouch = useTouchViewport();
		const touch = mode?.touch ?? viewportTouch;
		const content = (
			<PopoverPrimitive.Content
				ref={ref}
				align={align}
				sideOffset={sideOffset}
				collisionPadding={collisionPadding ?? (touch ? 8 : 0)}
				className={cn(
					'z-[60] w-72 max-md:max-w-[calc(100vw-1rem)] rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-popover-content-transform-origin]',
					// Below 1280px (phones, landscape phones only ~390px tall,
					// tablets) it never runs off the screen: it scrolls inside.
					'max-xl:max-h-[var(--radix-popover-content-available-height)] max-xl:overflow-y-auto max-xl:overscroll-contain',
					className
				)}
				onOpenAutoFocus={(event) => {
					if (onOpenAutoFocus) return onOpenAutoFocus(event);
					// Touch screens: focus the popover rather than a search field
					// in it, so the keyboard doesn't jump up over the list.
					if (touch) {
						event.preventDefault();
						(event.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
					}
				}}
				{...props}
			/>
		);

		if (!portal && !(touch && (mode?.modal ?? true))) {
			return content;
		}

		return <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal>;
	}
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
